import { useRef } from 'react';
import { usePermissions } from 'react-admin';

import { useSnackbar } from 'notistack';

import { api, getUserId } from '../../services';
import { GERAR_PACOTE_FISCAL_HABILITADO } from '../../utils/constants';
import {
  PERMISSAO_TELA_RELATORIOS_FISCAIS_GERAR,
  PERMISSAO_TELA_RELATORIOS_FISCAIS_REPROCESSAR_FORCADO,
} from '../../utils/dadosTelas';

// Ações de escrita/download da Etapa 7 (plano §6.9/§6.11). Controle de acesso
// só client-side (D6): a API não verifica permissão, então quem chamar o
// endpoint direto não é barrado — aqui só escondemos os botões.

const STATUS_FINALIZADO = [
  'sucesso',
  'sucesso_com_pendencias',
  'erro',
  'cancelada',
];

export const podeBaixar = (execucao) =>
  execucao.tipo === 'pacote_mensal' &&
  ['sucesso', 'sucesso_com_pendencias'].includes(execucao.status) &&
  !execucao.artefatos_removidos_em &&
  !!execucao.arquivo_zip_nome;

// Reprocessar uma execução de pacote cria um pacote novo, então também some
// quando a geração de pacote está desligada.
export const podeReprocessar = (execucao) =>
  STATUS_FINALIZADO.includes(execucao.status) &&
  (execucao.tipo !== 'pacote_mensal' || GERAR_PACOTE_FISCAL_HABILITADO);

export const podeCancelar = (execucao) =>
  ['pendente', 'processando'].includes(execucao.status);

export function usePermissoesRelatoriosFiscais() {
  const { permissions } = usePermissions();
  return {
    podeGerar: !!permissions?.[PERMISSAO_TELA_RELATORIOS_FISCAIS_GERAR],
    podeForcar: !!permissions?.[
      PERMISSAO_TELA_RELATORIOS_FISCAIS_REPROCESSAR_FORCADO
    ],
  };
}

// Recusa de regra de negócio da API (409/410/422/503): `dados` carrega, por
// exemplo, o execucao_id da execução que causou o 409.
export class ErroAcaoFiscal extends Error {
  constructor(message, status, dados) {
    super(message);
    this.status = status;
    this.dados = dados;
  }
}

async function extrairCorpoDoErro(erro) {
  const corpo = erro?.response?.data;
  // Download usa responseType 'blob': o JSON do erro chega como Blob.
  if (typeof Blob !== 'undefined' && corpo instanceof Blob) {
    try {
      return JSON.parse(await corpo.text());
    } catch (e) {
      return null;
    }
  }
  return corpo && typeof corpo === 'object' ? corpo : null;
}

async function chamar(requisicao, mensagemPadrao) {
  try {
    const response = await requisicao();
    return response;
  } catch (erro) {
    const corpo = await extrairCorpoDoErro(erro);
    throw new ErroAcaoFiscal(
      corpo?.message || mensagemPadrao,
      erro?.response?.status,
      corpo?.data || null,
    );
  }
}

export async function criarPacote({ ano, mes }) {
  const response = await chamar(
    () =>
      api.post('/relatorios_fiscais_execucoes/pacote', {
        ano,
        mes,
        usuario_id: getUserId(),
      }),
    'Não foi possível solicitar o pacote.',
  );
  return response.data.data;
}

export async function criarIngestao({ dataInicio, dataFim, forcado }) {
  const response = await chamar(
    () =>
      api.post('/relatorios_fiscais_execucoes/ingestao', {
        data_inicio: dataInicio,
        data_fim: dataFim,
        modo: forcado ? 'forcado' : 'normal',
        usuario_id: getUserId(),
      }),
    'Não foi possível solicitar a ingestão.',
  );
  return response.data.data;
}

export async function reprocessarExecucao(id) {
  const response = await chamar(
    () =>
      api.put(`/relatorios_fiscais_execucoes/reprocessar/${id}`, {
        usuario_id: getUserId(),
      }),
    'Não foi possível reprocessar a execução.',
  );
  return response.data.data;
}

export async function cancelarExecucao(id) {
  const response = await chamar(
    () =>
      api.put(`/relatorios_fiscais_execucoes/cancelar/${id}`, {
        usuario_id: getUserId(),
      }),
    'Não foi possível cancelar a execução.',
  );
  return response.data.data;
}

function nomeDoArquivo(response, execucao) {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : execucao.arquivo_zip_nome;
}

// Download por blob: a API responde 410 quando o artefato já expirou.
export async function baixarPacote(execucao) {
  const response = await chamar(
    () =>
      api.get(`/relatorios_fiscais_execucoes/download/${execucao.id}`, {
        responseType: 'blob',
      }),
    'Não foi possível baixar o pacote.',
  );

  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeDoArquivo(response, execucao);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Junta as ações usadas pelo histórico e pelo detalhe. Quem usar precisa
// renderizar <DialogoConfirmacao ref={refDialogoConfirmacao}
// handleConfirma={handleConfirma} />.
export function useAcoesExecucao({ onAtualizado, onCriada } = {}) {
  const { enqueueSnackbar } = useSnackbar();
  const refDialogoConfirmacao = useRef(null);

  async function executar(acao, mensagemSucesso) {
    try {
      const dados = await acao();
      if (mensagemSucesso) {
        enqueueSnackbar(mensagemSucesso, { variant: 'success' });
      }
      return { ok: true, dados };
    } catch (erro) {
      enqueueSnackbar(erro.message, { variant: 'error' });
      return { ok: false, dados: null };
    }
  }

  function pedirReprocessar(execucao) {
    if (!refDialogoConfirmacao.current) return;
    refDialogoConfirmacao.current.handleOpen(
      'REPROCESSAR EXECUÇÃO',
      `Criar uma nova execução com os mesmos parâmetros da execução #${execucao.id}?`,
      { id: execucao.id, codigo: 1 },
    );
  }

  function pedirCancelar(execucao) {
    if (!refDialogoConfirmacao.current) return;
    refDialogoConfirmacao.current.handleOpen(
      'CANCELAR EXECUÇÃO',
      `Tem certeza que deseja cancelar a execução #${execucao.id}?`,
      { id: execucao.id, codigo: 2 },
    );
  }

  async function handleConfirma({ id, codigo }) {
    if (codigo === 1) {
      const { ok, dados } = await executar(
        () => reprocessarExecucao(id),
        'Reprocessamento solicitado.',
      );
      if (ok && onCriada) onCriada(dados.execucao_id);
    }

    if (codigo === 2) {
      const { ok } = await executar(
        () => cancelarExecucao(id),
        'Cancelamento registrado.',
      );
      if (ok && onAtualizado) onAtualizado();
    }
  }

  function baixar(execucao) {
    return executar(() => baixarPacote(execucao), 'Download iniciado.');
  }

  return {
    refDialogoConfirmacao,
    pedirReprocessar,
    pedirCancelar,
    handleConfirma,
    baixar,
  };
}
